import {Marked, Tokens} from 'marked';
import {
  AiChatCollegamento, AiChatComando, AiChatContestoProposta, AiChatEsitoMessaggio, AiChatEvento, AiChatGrafico,
  AiChatMessaggio
} from '../../_models/ai-chat.model';
import {urlEntita} from '../../_helpers/entita-link';

// Feature 159: stato di visualizzazione della chat, indipendente dal componente (testabile da solo).

export interface ComandoVista extends AiChatComando {
  inCorso: boolean;
  avviatoIl: number;
}

export type FaseRisposta = 'pensando' | 'rispondendo' | 'conclusa';

export interface DomandaVista {
  ruolo: 'user';
  testo: string;
  createdAt: Date;
}

export interface RispostaVista {
  ruolo: 'assistant';
  testo: string;
  html: string;
  comandi: ComandoVista[];
  fase: FaseRisposta;
  esito?: AiChatEsitoMessaggio;
  errore?: string;
  inputTokens: number;
  outputTokens: number;
  createdAt: Date;
  grafici: AiChatGrafico[];
  /** Link verificati (feature 160); `undefined` finché la risposta non è conclusa. */
  collegamenti?: AiChatCollegamento[];
  /** Feature 164: ultima proposta di contesto della risposta (`proponi_contesto`). */
  proposta?: AiChatContestoProposta;
}

export type VoceChat = DomandaVista | RispostaVista;

/** Le date del backend sono UTC; senza fuso esplicito si aggiunge `Z` (come `UtcToLocalPipe`). */
export function dataUtc(valore: string): Date {
  return new Date(/[zZ]|[+-]\d{2}:\d{2}$/.test(valore) ? valore : `${valore}Z`);
}

const SCHEMA_ENTITA = 'entita:';
const MARCATORE_ENTITA = /\[([^\]]*)\]\(entita:[^)\s]*\)/g;

// Collegamenti della risposta in corso di rendering: `marked.parse` è sincrono, il renderer li legge da qui.
let collegamentiCorrenti: AiChatCollegamento[] | undefined;

const markdown = new Marked({async: false, gfm: true, breaks: true}, {renderer: {link: linkEntita}});

/** Markdown → HTML; il binding `[innerHTML]` di Angular sanifica il risultato (niente bypass). I marcatori
 * `entita:` (feature 160) diventano link solo se verificati in `collegamenti`; senza `collegamenti` (risposta
 * in arrivo) restano testo in attesa, con `collegamenti` ma non verificati testo semplice. */
export function rendiMarkdown(testo: string, collegamenti?: AiChatCollegamento[]): string {
  collegamentiCorrenti = collegamenti;
  try {
    return markdown.parse(testo) as string;
  } catch {
    return testo;
  } finally {
    collegamentiCorrenti = undefined;
  }
}

/** Testo senza marcatori `entita:` (solo il testo visibile), per «Copia risposta». */
export function testoSemplice(testo: string): string {
  return testo.replace(MARCATORE_ENTITA, '$1');
}

function linkEntita(this: {parser: {parseInline(tokens: Tokens.Generic[]): string}}, link: Tokens.Link): string | false {
  if (!link.href.startsWith(SCHEMA_ENTITA)) return false;
  const testo = this.parser.parseInline(link.tokens);
  if (!collegamentiCorrenti) return `<span class="ai-chat__entita-attesa">${testo}</span>`;
  const collegamento = collegamentiCorrenti.find(c => decodifica(c.marcatore) === decodifica(link.href));
  const url = collegamento ? urlEntita(collegamento.tipo, collegamento.chiavi) : null;
  return url ? `<a class="ai-chat__entita" href="${escapeAttributo(url)}">${testo}</a>` : testo;
}

function decodifica(valore: string): string {
  try {
    return decodeURIComponent(valore);
  } catch {
    return valore;
  }
}

function escapeAttributo(valore: string): string {
  return valore.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

export function nuovaRisposta(adesso = new Date()): RispostaVista {
  return {ruolo: 'assistant', testo: '', html: '', comandi: [], fase: 'pensando', inputTokens: 0, outputTokens: 0,
    createdAt: adesso, grafici: []};
}

/** Applica un evento SSE alla risposta in corso (contracts/sse-eventi.md). */
export function applicaEvento(risposta: RispostaVista, evento: AiChatEvento, adesso = Date.now()): void {
  switch (evento.type) {
    case 'status': risposta.fase = evento.stato; break;
    case 'chunk': aggiungiTesto(risposta, evento.testo); break;
    case 'tool_start': risposta.comandi.push(comandoAvviato(evento, adesso)); break;
    case 'tool_end': Object.assign(trovaComando(risposta, evento.id) ?? {}, chiusura(evento)); break;
    case 'usage': Object.assign(risposta, {inputTokens: evento.inputTokens, outputTokens: evento.outputTokens}); break;
    case 'grafico': aggiungiGrafico(risposta, evento); break;
    case 'contesto': risposta.proposta = proposta(evento); break;
    case 'done': concludiConCollegamenti(risposta, evento.collegamenti ?? [], evento.inputTokens, evento.outputTokens); break;
    case 'error': fallisci(risposta, evento.messaggio); break;
  }
}

/** Stop dell'operatore: il server salva il testo parziale come `interrotto`. */
export function interrompi(risposta: RispostaVista): void {
  chiudiComandiAperti(risposta, 'Interrotto');
  risposta.fase = 'conclusa';
  risposta.esito = 'interrotto';
  senzaCollegamenti(risposta);
}

export function fallisci(risposta: RispostaVista, messaggio: string): void {
  chiudiComandiAperti(risposta, messaggio);
  risposta.fase = 'conclusa';
  risposta.esito = 'errore';
  risposta.errore = messaggio;
  senzaCollegamenti(risposta);
}

// Risposta non conclusa: i marcatori `entita:` non sono verificati e restano testo semplice.
function senzaCollegamenti(risposta: RispostaVista): void {
  risposta.collegamenti = [];
  risposta.html = rendiMarkdown(risposta.testo, []);
}

export function daMessaggi(messaggi: AiChatMessaggio[]): VoceChat[] {
  return messaggi.map(m => m.ruolo === 'user'
    ? {ruolo: 'user', testo: m.contenuto, createdAt: dataUtc(m.createdAtUtc)}
    : rispostaSalvata(m));
}

/** Testo dell'indicatore di attesa mentre il modello lavora senza scrivere: prima dei comandi e dopo che
 * sono finiti (il modello legge i risultati). `null` mentre un comando è in corso o il testo arriva. */
export function attesa(risposta: RispostaVista): string | null {
  if (risposta.fase !== 'pensando' || risposta.comandi.some(c => c.inCorso)) return null;
  return risposta.comandi.length ? 'Sto elaborando i risultati…' : 'Sto analizzando la domanda…';
}

/** Nomi dei comandi usati, senza ripetizioni, per la riga «consultati». */
export function comandiConsultati(risposta: RispostaVista): string[] {
  return [...new Set(risposta.comandi.map(c => c.nome))];
}

function rispostaSalvata(m: AiChatMessaggio): RispostaVista {
  const comandi = (m.comandi ?? []).map(c => ({...c, inCorso: false, avviatoIl: 0}));
  const collegamenti = m.collegamenti ?? [];
  return {
    ruolo: 'assistant', testo: m.contenuto, html: rendiMarkdown(m.contenuto, collegamenti), comandi, fase: 'conclusa',
    esito: m.esito, errore: m.esito === 'errore' ? 'La risposta non è stata completata.' : undefined,
    inputTokens: m.inputTokens ?? 0, outputTokens: m.outputTokens ?? 0, createdAt: dataUtc(m.createdAtUtc),
    grafici: m.grafici ?? [], collegamenti, proposta: m.contestoProposto ?? undefined
  };
}

function proposta(evento: Extract<AiChatEvento, {type: 'contesto'}>): AiChatContestoProposta {
  const {type: _tipoEvento, ...dati} = evento;
  return dati;
}

function aggiungiGrafico(risposta: RispostaVista, evento: Extract<AiChatEvento, {type: 'grafico'}>): void {
  if (risposta.grafici.some(g => g.id === evento.id)) return;
  const {type: _tipoEvento, ...grafico} = evento;
  risposta.grafici.push(grafico);
}

function concludiConCollegamenti(risposta: RispostaVista, collegamenti: AiChatCollegamento[], input: number,
                                 output: number): void {
  concludi(risposta, 'ok', input, output);
  risposta.collegamenti = collegamenti;
  risposta.html = rendiMarkdown(risposta.testo, collegamenti);
}

function aggiungiTesto(risposta: RispostaVista, testo: string): void {
  risposta.fase = 'rispondendo';
  risposta.testo += testo;
  risposta.html = rendiMarkdown(risposta.testo);
}

function comandoAvviato(evento: Extract<AiChatEvento, {type: 'tool_start'}>, adesso: number): ComandoVista {
  return {id: evento.id, nome: evento.nome, etichetta: evento.etichetta, parametri: evento.parametri ?? [],
    esito: 'ok', durataMs: 0, inCorso: true, avviatoIl: adesso};
}

function chiusura(evento: Extract<AiChatEvento, {type: 'tool_end'}>): Partial<ComandoVista> {
  return {inCorso: false, esito: evento.esito, durataMs: evento.durataMs, righe: evento.righe,
    messaggio: evento.messaggio};
}

function trovaComando(risposta: RispostaVista, id: string): ComandoVista | undefined {
  return risposta.comandi.find(c => c.id === id);
}

function concludi(risposta: RispostaVista, esito: AiChatEsitoMessaggio, input: number, output: number): void {
  Object.assign(risposta, {fase: 'conclusa', esito, inputTokens: input, outputTokens: output});
}

function chiudiComandiAperti(risposta: RispostaVista, messaggio: string): void {
  risposta.comandi.filter(c => c.inCorso).forEach(c => Object.assign(c, {inCorso: false, esito: 'errore', messaggio}));
}

export interface GruppoConversazioni<T> {
  etichetta: string;
  voci: T[];
}

const GRUPPI = ['Oggi', 'Ieri', 'Ultimi 7 giorni', 'Più vecchie'];

/** Archivio: gruppi per giorno locale di ultimo aggiornamento, nell'ordine ricevuto (dal più recente). */
export function raggruppaConversazioni<T extends {updatedAtUtc: string}>(voci: T[], oggi = new Date()):
  GruppoConversazioni<T>[] {
  const gruppi = GRUPPI.map(etichetta => ({etichetta, voci: [] as T[]}));
  voci.forEach(voce => gruppi[indiceGruppo(giorniFa(dataUtc(voce.updatedAtUtc), oggi))].voci.push(voce));
  return gruppi.filter(g => g.voci.length > 0);
}

/** Ora per oggi e ieri, giorno della settimana negli ultimi 7 giorni, data per le più vecchie. */
export function quandoBreve(data: Date, oggi = new Date()): string {
  const giorni = giorniFa(data, oggi);
  const opzioni: Intl.DateTimeFormatOptions = giorni <= 1 ? {hour: '2-digit', minute: '2-digit'}
    : giorni <= 7 ? {weekday: 'short', day: 'numeric', month: 'short'} : {day: 'numeric', month: 'short'};
  return new Intl.DateTimeFormat('it-IT', opzioni).format(data);
}

function giorniFa(data: Date, oggi: Date): number {
  const inizio = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((inizio(oggi) - inizio(data)) / 86_400_000);
}

function indiceGruppo(giorni: number): number {
  if (giorni <= 0) return 0;
  if (giorni === 1) return 1;
  return giorni <= 7 ? 2 : 3;
}
